import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { formatCurrency, formatDate, getDatePreset } from '../utils/formatters';
import DateRangeFilter from '../components/DateRangeFilter';
import ExportButton from '../components/ExportButton';
import KPICard from '../components/KPICard';

export default function Payouts() {
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dateRange, setDateRange] = useState(getDatePreset('lifetime'));
  const [loading, setLoading] = useState(true);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/payouts', { params: dateRange });
      setPayouts(data.payouts);
      setSummary(data.summary);
    } catch (err) { console.error('Payouts fetch error:', err); }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { fetchPayouts(); }, [fetchPayouts]);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Payout Reconciliation</h2>
        <ExportButton endpoint="/api/payouts/export" params={dateRange} filename="payouts-export.csv" />
      </div>

      <DateRangeFilter onChange={setDateRange} initial="lifetime" />

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><div className="spinner" /></div>}

      {!loading && summary && (
        <div className="kpi-section">
          <div className="kpi-grid">
            <KPICard label="Total Settled" value={formatCurrency(summary.total_settled)} />
            <KPICard label="Total Pending" value={formatCurrency(summary.total_pending)} />
            <KPICard label="Paid Statements" value={summary.paid_count} />
            <KPICard label="Pending Statements" value={summary.pending_count} />
          </div>
        </div>
      )}

      {!loading && <div className="card">
        <div className="table-container sticky-col">
          <table>
            <thead>
              <tr>
                <th>Statement Date</th>
                <th>Revenue</th>
                <th>Fees</th>
                <th>Shipping</th>
                <th>Settlement</th>
                <th>Order Sum</th>
                <th>Difference</th>
                <th>Orders</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map(p => {
                const diff = p.order_settlement_sum != null ? p.settlement_amount - p.order_settlement_sum : null;
                const hasFees = p.orders_with_fees > 0;
                const coveragePct = p.order_count > 0 ? Math.round(p.orders_with_fees / p.order_count * 100) : 0;
                return (
                  <tr key={p.id}>
                    <td>{formatDate(p.statement_time)}</td>
                    <td>{formatCurrency(p.revenue_amount)}</td>
                    <td style={{ color: '#e53e3e' }}>{formatCurrency(p.fee_amount)}</td>
                    <td>{formatCurrency(p.shipping_cost_amount)}</td>
                    <td style={{ fontWeight: 600 }}>{formatCurrency(p.settlement_amount)}</td>
                    <td>{hasFees ? formatCurrency(p.order_settlement_sum) : <span style={{ color: 'var(--text-light)', fontSize: '12px' }}>Pending</span>}</td>
                    <td style={{ color: diff != null && Math.abs(diff) > 1 ? '#e53e3e' : diff != null ? '#38a169' : 'var(--text-light)' }}>
                      {hasFees ? formatCurrency(diff) : '—'}
                    </td>
                    <td>
                      <span>{p.order_count}</span>
                      {p.order_count > 0 && <span style={{ fontSize: '11px', color: 'var(--text-light)', marginLeft: '4px' }}>({coveragePct}% reconciled)</span>}
                    </td>
                    <td>
                      <span className={`badge ${p.payment_status === 'PAID' ? 'badge-success' : 'badge-warning'}`}>
                        {p.payment_status || 'PENDING'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {payouts.length === 0 && (
                <tr><td colSpan="9" style={{ textAlign: 'center', color: 'var(--text-light)' }}>No payouts found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>}
    </div>
  );
}
